import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Bio } from '../../data/constants';
import {
  HeroContainer, CanvasWrapper, Content,
  OrbTopRight, OrbBottomLeft,
  Badge, Eyebrow,
  Name, RoleRow, RoleText, Description,
  CTARow, PrimaryBtn, SecondaryBtn,
  StatsDivider, StatsRow, Stat, StatValue, StatLabel,
  SocialRow, SocialLink, ScrollHint,
} from './HeroStyle';
import { GitHub, LinkedIn, Twitter, Instagram } from '@mui/icons-material';

gsap.registerPlugin(ScrollTrigger);

const PARTICLE_COUNT = 100;
const CONNECT_DIST = 140;

const STATS = [
  { value: 3,  suffix: '+', label: 'Years Coding' },
  { value: 10, suffix: '+', label: 'Projects Built' },
  { value: 5,  suffix: '+', label: 'Tech Stacks' },
];

const HeroSection = () => {
  const mountRef   = useRef(null);
  const badgeRef   = useRef(null);
  const eyebrowRef = useRef(null);
  const nameRef    = useRef(null);
  const roleRef    = useRef(null);
  const descRef    = useRef(null);
  const ctaRef     = useRef(null);
  const statsRef   = useRef(null);
  const socialRef  = useRef(null);

  // ── Three.js particle network ───────────────────────────────────────────
  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    const width  = container.clientWidth;
    const height = container.clientHeight;

    const scene  = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(
      -width / 2, width / 2, height / 2, -height / 2, 0.1, 1000
    );
    camera.position.z = 500;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    const positions  = new Float32Array(PARTICLE_COUNT * 3);
    const velocities = [];

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      positions[i * 3]     = (Math.random() - 0.5) * width;
      positions[i * 3 + 1] = (Math.random() - 0.5) * height;
      positions[i * 3 + 2] = 0;
      velocities.push({
        x: (Math.random() - 0.5) * 0.5,
        y: (Math.random() - 0.5) * 0.5,
      });
    }

    const particleGeo = new THREE.BufferGeometry();
    particleGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const particleMat = new THREE.PointsMaterial({
      color: 0x22d3ee, size: 2.5, transparent: true, opacity: 0.65, sizeAttenuation: false,
    });

    scene.add(new THREE.Points(particleGeo, particleMat));

    const maxLineVerts = PARTICLE_COUNT * (PARTICLE_COUNT - 1);
    const linePositions = new Float32Array(maxLineVerts * 3);
    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute('position', new THREE.BufferAttribute(linePositions, 3));
    lineGeo.setDrawRange(0, 0);

    const lineMat = new THREE.LineBasicMaterial({ color: 0x22d3ee, transparent: true, opacity: 0.1 });
    const lineSegments = new THREE.LineSegments(lineGeo, lineMat);
    scene.add(lineSegments);

    const mouse = { x: 0, y: 0 };
    const onMouseMove = (e) => {
      const rect = container.getBoundingClientRect();
      mouse.x =  (e.clientX - rect.left) - width  / 2;
      mouse.y = -(e.clientY - rect.top)  + height / 2;
    };
    container.addEventListener('mousemove', onMouseMove);

    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      const pos = particleGeo.attributes.position.array;

      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const ix = i * 3;
        const dx = mouse.x - pos[ix];
        const dy = mouse.y - pos[ix + 1];
        const d  = Math.sqrt(dx * dx + dy * dy);

        if (d < 180) {
          velocities[i].x += (dx / d) * 0.00012;
          velocities[i].y += (dy / d) * 0.00012;
        }

        velocities[i].x *= 0.99;
        velocities[i].y *= 0.99;
        const spd = Math.sqrt(velocities[i].x ** 2 + velocities[i].y ** 2);
        if (spd > 0.9) {
          velocities[i].x = (velocities[i].x / spd) * 0.9;
          velocities[i].y = (velocities[i].y / spd) * 0.9;
        }

        pos[ix]     += velocities[i].x;
        pos[ix + 1] += velocities[i].y;

        if (pos[ix]     >  width  / 2) pos[ix]     = -width  / 2;
        if (pos[ix]     < -width  / 2) pos[ix]     =  width  / 2;
        if (pos[ix + 1] >  height / 2) pos[ix + 1] = -height / 2;
        if (pos[ix + 1] < -height / 2) pos[ix + 1] =  height / 2;
      }

      particleGeo.attributes.position.needsUpdate = true;

      let lineIdx = 0;
      const lp = lineGeo.attributes.position.array;
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        for (let j = i + 1; j < PARTICLE_COUNT; j++) {
          const ddx = pos[i * 3] - pos[j * 3];
          const ddy = pos[i * 3 + 1] - pos[j * 3 + 1];
          if (Math.sqrt(ddx * ddx + ddy * ddy) < CONNECT_DIST) {
            lp[lineIdx++] = pos[i * 3];     lp[lineIdx++] = pos[i * 3 + 1];     lp[lineIdx++] = 0;
            lp[lineIdx++] = pos[j * 3];     lp[lineIdx++] = pos[j * 3 + 1];     lp[lineIdx++] = 0;
          }
        }
      }

      lineGeo.setDrawRange(0, lineIdx / 3);
      lineGeo.attributes.position.needsUpdate = true;
      renderer.render(scene, camera);
    };

    animate();

    const onResize = () => {
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.left = -w / 2; camera.right = w / 2;
      camera.top  =  h / 2; camera.bottom = -h / 2;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(animId);
      container.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('resize', onResize);
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      renderer.dispose();
      particleGeo.dispose(); lineGeo.dispose();
      particleMat.dispose(); lineMat.dispose();
    };
  }, []);

  // ── GSAP entrance reveal + name scramble ────────────────────────────────
  useEffect(() => {
    const CHARS = '!<>—_\\/[]{}=+*^?#@ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    let rafId;

    const scramble = (el, finalText, delay = 0) => {
      const totalFrames = 28;
      let frame = 0;
      const run = () => {
        const resolved = Math.floor((frame / totalFrames) * finalText.length);
        el.textContent = finalText
          .split('')
          .map((ch, i) => {
            if (ch === ' ') return ' ';
            if (i < resolved) return ch;
            return CHARS[Math.floor(Math.random() * CHARS.length)];
          })
          .join('');
        frame++;
        if (frame <= totalFrames) rafId = requestAnimationFrame(run);
        else el.textContent = finalText;
      };
      setTimeout(() => requestAnimationFrame(run), delay);
    };

    const ctx = gsap.context(() => {
      const targets = [
        badgeRef.current, eyebrowRef.current, nameRef.current,
        roleRef.current, descRef.current, ctaRef.current,
        statsRef.current, socialRef.current,
      ].filter(Boolean);

      gsap.set(targets, { opacity: 0, y: 36 });

      gsap.timeline({ delay: 0.2 })
        .to(badgeRef.current,   { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out' })
        .to(eyebrowRef.current, { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out' }, '-=0.25')
        .to(nameRef.current,    { opacity: 1, y: 0, duration: 0.9, ease: 'power4.out' }, '-=0.3',
          () => scramble(nameRef.current, Bio.name, 0))
        .to(roleRef.current,    { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out' }, '-=0.5')
        .to(descRef.current,    { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }, '-=0.4')
        .to(ctaRef.current,     { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out' }, '-=0.3')
        .to(statsRef.current,   { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out' }, '-=0.2')
        .to(socialRef.current,  { opacity: 1, y: 0, duration: 0.4, ease: 'power3.out' }, '-=0.2');
    });

    return () => {
      ctx.revert();
      cancelAnimationFrame(rafId);
    };
  }, []);

  // ── Animated stat counters ──────────────────────────────────────────────
  useEffect(() => {
    const ctx = gsap.context(() => {
      const counterEls = document.querySelectorAll('.stat-val');
      counterEls.forEach((el) => {
        const target = Number(el.dataset.target);
        const obj = { val: 0 };
        gsap.to(obj, {
          val: target,
          duration: 1.8,
          ease: 'power2.out',
          delay: 1.4,
          onUpdate: () => { el.textContent = Math.round(obj.val) + (el.dataset.suffix || ''); },
        });
      });
    });

    return () => ctx.revert();
  }, []);

  // ── Magnetic button effect ──────────────────────────────────────────────
  useEffect(() => {
    const ctaEl = ctaRef.current;
    const ctx = gsap.context(() => {
      const btns = ctaEl?.querySelectorAll('a');
      if (!btns) return;

      btns.forEach((btn) => {
        const onMove = (e) => {
          const rect = btn.getBoundingClientRect();
          const cx = rect.left + rect.width  / 2;
          const cy = rect.top  + rect.height / 2;
          const dx = (e.clientX - cx) * 0.22;
          const dy = (e.clientY - cy) * 0.22;
          gsap.to(btn, { x: dx, y: dy, duration: 0.3, ease: 'power2.out' });
        };
        const onLeave = () => gsap.to(btn, { x: 0, y: 0, duration: 0.5, ease: 'elastic.out(1, 0.5)' });

        btn.addEventListener('mousemove', onMove);
        btn.addEventListener('mouseleave', onLeave);

        btn._clean = () => {
          btn.removeEventListener('mousemove', onMove);
          btn.removeEventListener('mouseleave', onLeave);
        };
      });
    });

    return () => {
      ctx.revert();
      ctaEl?.querySelectorAll('a').forEach((btn) => btn._clean?.());
    };
  }, []);

  return (
    <div id="about">
      <HeroContainer>
        <OrbTopRight />
        <OrbBottomLeft />
        <CanvasWrapper ref={mountRef} />

        <Content>
          <Badge ref={badgeRef}>
            <span className="dot" />
            Available for work
          </Badge>

          <Eyebrow ref={eyebrowRef}>Full-Stack Developer</Eyebrow>

          <Name ref={nameRef}>{Bio.name}</Name>

          <RoleRow ref={roleRef}>
            {Bio.roles.map((role, i) => (
              <RoleText key={i}>
                {role}
                {i < Bio.roles.length - 1 && <span className="sep">·</span>}
              </RoleText>
            ))}
          </RoleRow>

          <Description ref={descRef}>{Bio.description}</Description>

          <CTARow ref={ctaRef}>
            <PrimaryBtn href={Bio.resume} target="_blank" rel="noopener noreferrer">
              View Resume ↗
            </PrimaryBtn>
            <SecondaryBtn href={`#contact`}>
              Let's Talk →
            </SecondaryBtn>
          </CTARow>

          <StatsDivider />

          <StatsRow ref={statsRef}>
            {STATS.map(({ value, suffix, label }) => (
              <Stat key={label}>
                <StatValue
                  className="stat-val"
                  data-target={value}
                  data-suffix={suffix}
                >
                  0{suffix}
                </StatValue>
                <StatLabel>{label}</StatLabel>
              </Stat>
            ))}
          </StatsRow>

          <SocialRow ref={socialRef}>
            <SocialLink href={Bio.linkedin} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <LinkedIn style={{ fontSize: 18 }} />
            </SocialLink>
            <SocialLink href={Bio.github} target="_blank" rel="noopener noreferrer" aria-label="GitHub">
              <GitHub style={{ fontSize: 18 }} />
            </SocialLink>
            <SocialLink href={Bio.twitter} target="_blank" rel="noopener noreferrer" aria-label="Twitter">
              <Twitter style={{ fontSize: 18 }} />
            </SocialLink>
            <SocialLink href={Bio.insta} target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <Instagram style={{ fontSize: 18 }} />
            </SocialLink>
          </SocialRow>
        </Content>

        <ScrollHint>
          <span>scroll</span>
          <div className="line" />
        </ScrollHint>
      </HeroContainer>
    </div>
  );
};

export default HeroSection;
